/**
 * api.ts — the ONLY place fetch is called from in the app (per frontend-conventions.md §16).
 * Feature-level api.ts files compose these helpers; they MUST NOT reach for global fetch.
 *
 * Responsibilities:
 *   - JSON body parsing
 *   - Error envelope unwrapping (matches backend convention: { error: { code, message, details? } })
 *   - 401 → global auth-cleared + redirect (wired once auth store is in place)
 *   - Same-origin cookie auth (HttpOnly session cookie from backend)
 */

import { z } from 'zod';

const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(public override readonly cause: unknown) {
    super('Network error');
    this.name = 'NetworkError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, headers } = options;

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    let envelope: z.infer<typeof ErrorEnvelopeSchema> | null = null;
    try {
      envelope = ErrorEnvelopeSchema.parse(await response.json());
    } catch {
      // Body wasn't a valid error envelope — surface a generic ApiError.
    }
    throw new ApiError(
      response.status,
      envelope?.error.code ?? `HTTP_${response.status}`,
      envelope?.error.message ?? response.statusText,
      envelope?.error.details,
    );
  }

  // 204 No Content — caller passed an empty schema (z.void() or similar)
  if (response.status === 204) {
    return schema.parse(undefined);
  }

  const raw = (await response.json()) as unknown;
  return schema.parse(raw);
}

export const api = {
  get: <T>(path: string, schema: z.ZodType<T>, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request(path, schema, { ...opts, method: 'GET' }),
  post: <T>(
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ) => request(path, schema, { ...opts, method: 'POST', body }),
  patch: <T>(
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ) => request(path, schema, { ...opts, method: 'PATCH', body }),
  put: <T>(
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ) => request(path, schema, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, schema: z.ZodType<T>, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request(path, schema, { ...opts, method: 'DELETE' }),
};
